<script setup lang="ts">
import {
    computed,
    ref,
    type Component,
} from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import Popover from 'primevue/popover';
import defaultLogo from '@/assets/logo.png';
import IconDashboard from '@/components/icons/IconDashboard.vue';
import IconPayments from '@/components/icons/IconPayments.vue';
import IconAccounts from '@/components/icons/IconAccounts.vue';
import IconNegotiations from '@/components/icons/IconNegotiations.vue';
import IconReports from '@/components/icons/IconReports.vue';
import IconUserMenuAccount from '@/components/icons/IconUserMenuAccount.vue';
import IconUserMenuLogout from '@/components/icons/IconUserMenuLogout.vue';
import NotificationDrawer from '@/components/notifications/NotificationDrawer.vue';
import HelpSupportModal from '@/components/modals/HelpSupportModal.vue';
import { useAuthStore } from '@/stores/auth';
import { useAccountStore } from '@/stores/account';
import { useSettingsStore } from '@/stores/settings';
import { useNotificationsStore } from '@/stores/notifications';
import { useProductTourStore } from '@/stores/productTour';
import { usePermissions } from '@/composables/usePermissions';
import type { ModuleKey } from '@/permissions';

interface NavItem {
    label: string
    icon: Component
    path: string
    module: ModuleKey
    exactMatch?: boolean
}

interface UserMenuAction {
    id: 'account' | 'logout'
    label: string
    icon: Component
}

const authStore = useAuthStore();
const accountStore = useAccountStore();
const settingsStore = useSettingsStore();
const notificationsStore = useNotificationsStore();
const productTour = useProductTourStore();
const { user } = storeToRefs(authStore);
const { hasUnread } = storeToRefs(notificationsStore);
const { openDrawer } = notificationsStore;
const route = useRoute();
const router = useRouter();
const userName = computed(() => user.value?.name || '');
const userCompany = computed(() => settingsStore.companyName || '');
const userInitials = computed(() => {
    const parts = userName.value.trim().split(/\s+/);
    return parts.length >= 2
        ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        : parts[0].slice(0, 2).toUpperCase();
});

const userMenu = ref();
const userMenuActions: UserMenuAction[] = [
    { id: 'account', label: 'My Account', icon: IconUserMenuAccount },
    { id: 'logout', label: 'Logout', icon: IconUserMenuLogout },
];

const isUserMenuOpen = ref(false);
const isHelpModalVisible = ref(false);

function replayProductTour() {
    isHelpModalVisible.value = false;
    productTour.requestReplay();
}

const createHeaderTooltip = (label: string) => ({
    value: label,
    class: 'app-header-tooltip',
    showDelay: 80,
});

const notificationsTooltip = createHeaderTooltip('Notifications');
const settingsTooltip = createHeaderTooltip('Settings');
const helpTooltip = createHeaderTooltip('Help & Support');

const handleUserMenuAction = async (actionId: 'account' | 'logout') => {
    if (actionId === 'account') {
        router.push({ name: 'my-account' });
    } else if (actionId === 'logout') {
        await authStore.logoutUser();
    }

    userMenu.value?.hide();
};

const toggleUserMenu = (event: MouseEvent) => {
    userMenu.value?.toggle(event);
};

const navItems: NavItem[] = [
    {
        label: 'Dashboard',
        icon: IconDashboard,
        path: '/',
        module: 'dashboard',
        exactMatch: true,
    },
    { label: 'Payments', icon: IconPayments, path: '/payments', module: 'payments' },
    // PHASE2
    // { label: 'Accounts', icon: IconAccounts, path: '/accounts', module: 'accounts' },
    // { label: 'Negotiations', icon: IconNegotiations, path: '/negotiations', module: 'negotiations' },
    { label: 'Reports', icon: IconReports, path: '/reports', module: 'reports' },
];

const { can } = usePermissions();
const visibleNavItems = computed(() => navItems.filter((item) => can(item.module)));
</script>

<template>
    <header class="app-header">
        <div class="app-header__bar">
            <div class="app-header__logo">
                <img
                    :src="defaultLogo"
                    alt="Logo"
                    class="app-header__logo-img"
                    @click="$router.push('/')"
                />
            </div>

            <nav class="app-header__nav" aria-label="Main navigation">
                <RouterLink
                    v-for="item in visibleNavItems"
                    :key="item.path"
                    :to="item.path"
                    :data-product-tour="`nav-${item.module}`"
                    class="app-header__nav-item"
                    :class="{
                        'app-header__nav-item--active': item.exactMatch
                            ? route.path === item.path
                            : route.path === item.path || route.path.startsWith(item.path + '/'),
                    }"
                    active-class=""
                    exact-active-class=""
                >
                    <component :is="item.icon" class="app-header__nav-icon" aria-hidden="true" />
                    <span :data-text="item.label">{{ item.label }}</span>
                </RouterLink>
            </nav>

            <div class="app-header__right">
                <div class="app-header__actions">
                    <!-- PHASE1: general role has no notifications access. -->
                    <button v-if="can('notifications')" class="app-header__action-btn" data-product-tour="notifications" aria-label="Notifications" @click="openDrawer" v-tooltip.bottom="notificationsTooltip">
                        <span class="app-header__notify">
                            <i class="pi pi-bell" aria-hidden="true" />
                            <span v-if="hasUnread" class="app-header__notify-dot" />
                        </span>
                    </button>

                    <button v-if="can('settings')" class="app-header__action-btn" data-product-tour="settings" aria-label="Settings" @click="router.push({ name: 'settings' })" v-tooltip.bottom="settingsTooltip">
                        <i class="pi pi-cog" aria-hidden="true" />
                    </button>

                    <button class="app-header__action-btn" data-product-tour="help" aria-label="Help" v-tooltip.bottom="helpTooltip" @click="isHelpModalVisible = true">
                        <i class="pi pi-question-circle" aria-hidden="true" />
                    </button>
                </div>

                <span class="app-header__divider" aria-hidden="true" />

                <div class="app-header__user-menu">
                    <button
                        class="app-header__user"
                        data-product-tour="account"
                        :class="{ 'app-header__user--open': isUserMenuOpen }"
                        type="button"
                        aria-label="User menu"
                        aria-haspopup="menu"
                        @click="toggleUserMenu"
                    >
                        <img
                            v-if="accountStore.displayAvatarUrl"
                            :src="accountStore.displayAvatarUrl"
                            alt="User avatar"
                            class="app-header__user-avatar app-header__user-avatar--img"
                        />
                        <span v-else class="app-header__user-avatar">{{ userInitials }}</span>
                        <span class="app-header__user-info">
                            <span :title="userName">{{ userName }}</span>
                            <span :title="userCompany">{{ userCompany }}</span>
                        </span>
                        <i class="pi pi-angle-down" aria-hidden="true" />
                    </button>

                    <Popover
                        ref="userMenu"
                        @show="isUserMenuOpen = true"
                        @hide="isUserMenuOpen = false"
                        :pt="{
                            root: { class: 'user-menu-popover' },
                            content: { class: 'user-menu-popover__content' },
                        }"
                    >
                        <div class="user-menu" role="menu" aria-label="User actions">
                            <template
                                v-for="(action, index) in userMenuActions"
                                :key="action.id"
                            >
                                <button
                                    class="user-menu__action"
                                    type="button"
                                    role="menuitem"
                                    @click="handleUserMenuAction(action.id)"
                                >
                                    <component
                                        :is="action.icon"
                                        class="user-menu__action-icon"
                                        aria-hidden="true"
                                    />
                                    <span>{{ action.label }}</span>
                                </button>
                                <span
                                    v-if="index < userMenuActions.length - 1"
                                    class="user-menu__divider"
                                    aria-hidden="true"
                                />
                            </template>
                        </div>
                    </Popover>
                </div>
            </div>
        </div>

        <NotificationDrawer />
        <HelpSupportModal
            v-model:visible="isHelpModalVisible"
            :can-replay-tour="productTour.eligible"
            @replay-tour="replayProductTour"
        />
    </header>
</template>

<style lang="scss" scoped>
.app-header {
    padding-top: $header-top-offset;

    &__bar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 20px;
        min-width: 0;
        height: $header-height;
        padding: 0 32px 0 35px;
        background: $header-gradient;
        border-radius: $header-border-radius;

        @media (max-width: 760px) {
            padding: 0 20px;
            gap: 12px;
        }

        @media (max-width: 600px) {
            padding: 0 16px;
            gap: 6px;
        }

        @media (max-width: 480px) {
            padding: 0 12px;
            gap: 4px;
        }
    }

    // ── Logo
    &__logo {
        position: relative;
        flex-shrink: 0;
        width: 195px;
        height: 50px;

        @media (max-width: 760px) {
            width: 140px;
            height: 36px;
        }

        @media (max-width: 600px) {
            width: 100px;
            height: 26px;
        }

        @media (max-width: 480px) {
            width: 80px;
            height: 21px;
        }
    }

    &__logo-img {
        width: 100%;
        height: 100%;
        display: block;
        object-fit: contain;
        cursor: pointer;
    }

    // ── Nav
    &__nav {
        display: flex;
        align-items: center;
        justify-content: center;
        flex: 1 1 auto;
        gap: clamp(10px, 2.5vw, 36px);
        min-width: 0;

        @media (max-width: 760px) {
            gap: 16px;
        }

        @media (max-width: 600px) {
            gap: 10px;
        }

        @media (max-width: 480px) {
            gap: 10px;
        }
    }

    &__nav-item {
        display: flex;
        align-items: center;
        padding: 2px 0;
        gap: clamp(6px, 1vw, 14px);
        font-family: $font-family-archivo;
        font-weight: 400;
        font-size: clamp(16px, 1.5vw, 19px);
        line-height: 1.1;
        color: #fff;
        opacity: 0.8;
        cursor: pointer;
        transition: opacity 0.15s ease;
        text-decoration: none;
        white-space: nowrap;
        flex-shrink: 1;
        min-width: 0;

        @media (max-width: 990px) {
            span {
                display: none;
            }
        }

        span {
            &::after {
                content: attr(data-text);
                font-weight: 600;
                display: block;
                height: 0;
                overflow: hidden;
                visibility: hidden;
            }
        }

        &:hover {
            opacity: 1;
        }

        &--active {
            opacity: 1;
            font-weight: 600;
            text-decoration: underline;
            text-underline-offset: 4px;
        }
    }

    &__nav-icon {
        display: block;
        flex-shrink: 0;

        @media (min-width: 991px) and (max-width: 1450px) {
            display: none;
        }

        @media (max-width: 600px) {
            width: 20px;
            height: 20px;
        }

        @media (max-width: 480px) {
            width: 18px;
            height: 18px;
        }
    }

    &__right {
        display: flex;
        align-items: center;
        gap: clamp(12px, 2vw, 45px);
        margin-left: auto;
        //flex-shrink: 0;
        min-width: 50px;

        @media (max-width: 760px) {
            gap: 16px;
        }

        @media (max-width: 600px) {
            gap: 10px;
            padding-left: 10px;
        }

        @media (max-width: 480px) {
            gap: 2px;
        }
    }

    &__actions {
        display: flex;
        align-items: center;
        gap: clamp(8px, 1vw, 22px);
        flex-shrink: 0;

        @media (max-width: 760px) {
            gap: 10px;
        }

        @media (max-width: 600px) {
            gap: 6px;
        }

        @media (max-width: 480px) {
            gap: 10px;
        }
    }

    &__divider {
        display: block;
        width: 1px;
        height: 50px;
        background: rgba(255, 255, 255, 0.3);
    }

    &__action-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        background: none;
        border: none;
        cursor: pointer;
        color: #fff;
        padding: 0;
        line-height: 1;

        .pi {
            font-size: 26px;

            @media (max-width: 760px) {
                font-size: 22px;
            }

            @media (max-width: 600px) {
                font-size: 20px;
            }

            @media (max-width: 480px) {
                font-size: 18px;
            }
        }

        &:hover {
            opacity: 0.8;
        }
    }

    &__notify {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    &__notify-dot {
        position: absolute;
        top: -3px;
        right: -4px;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: $color-secondary;
    }

    // ── User panel
    &__user-menu {
        position: relative;
        display: flex;
        flex: 0 1 auto;
        min-width: 80px;
        //max-width: clamp(200px, 25vw, 320px);
        max-width: 250px;

        @media (max-width: 760px) {
            min-width: 0px;
        }
        @media (max-width: 500px) {
            min-width: 80px;
        }
    }

    &__user {
        display: flex;
        align-items: center;
        gap: clamp(8px, 1vw, 14px);
        width: 100%;
        background: none;
        border: none;
        cursor: pointer;
        color: #fff;
        padding: 0;
        min-width: 0;

        @media (max-width: 760px) {
            gap: 8px;
        }

        @media (max-width: 600px) {
            gap: 6px;
        }

        @media (max-width: 480px) {
            gap: 4px;
        }
    }

    &__user-avatar {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        width: 46px;
        height: 46px;
        border-radius: 50%;
        background: $color-user-avatar-bg;
        font-family: $font-family-archivo;
        font-weight: 400;
        font-size: 14px;
        letter-spacing: 0.04em;
        color: rgba(0, 0, 0, 0.5);

        @media (max-width: 760px) {
            width: 40px;
            height: 40px;
            font-size: 13px;
        }

        @media (max-width: 600px) {
            width: 34px;
            height: 34px;
            font-size: 12px;
        }

        @media (max-width: 480px) {
            width: 30px;
            height: 30px;
            font-size: 11px;
        }

        &--img {
            object-fit: cover;
        }
    }

    &__user-info {
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
        min-width: 0;
        overflow: hidden;
        color: #fff;
        text-align: left;

        @media (max-width: 1200px) {
            display: none;
        }

        span:first-child,
        span:last-child {
            display: block;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        span:first-child {
            font-family: $font-family-archivo;
            font-weight: 500;
            font-size: 20px;
            line-height: 1.2;
        }

        span:last-child {
            font-family: $font-family-archivo;
            font-weight: 300;
            font-size: 14px;
            line-height: 1.2;
            opacity: 0.8;
        }
    }

    &__user > .pi {
        flex-shrink: 0;
        font-size: 14px;
        letter-spacing: -0.03em;
        transition: transform 0.2s ease;

        @media (max-width: 760px) {
            font-size: 12px;
        }

        @media (max-width: 600px) {
            font-size: 11px;
        }

        @media (max-width: 480px) {
            font-size: 10px;
        }
    }

    &__user--open > .pi {
        transform: rotate(180deg);
    }
}

.user-menu {
    display: flex;
    flex-direction: column;
    width: 100%;
    min-height: 100%;

    &__action {
        display: flex;
        align-items: center;
        gap: 15px;
        width: 100%;
        min-height: 48px;
        padding: 0 30px;
        border: none;
        background: transparent;
        font-family: $font-family-archivo;
        font-weight: 300;
        font-size: 16px;
        line-height: 18px;
        letter-spacing: -0.03em;
        color: $color-text-primary;
        text-align: left;
        cursor: pointer;
    }

    &__action-icon {
        display: block;
        flex-shrink: 0;
        color: $color-secondary;
    }

    &__divider {
        height: 1px;
        margin: 0 26px 0 59px;
        background: $color-divider-subtle;
    }
}

:global(.p-popover.user-menu-popover) {
    --p-popover-background: #{$color-surface-white};
    --p-popover-border-color: transparent;
    --p-popover-color: #{$color-text-primary};
    --p-popover-border-radius: 10px;
    --p-popover-shadow: 8px 10px 50px rgba(0, 0, 0, 0.05);
    --p-popover-gutter: 15px;
    --p-popover-content-padding: 10px 0;
    width: 250px;
    min-height: 117px;
    margin-left: 0;
    right: $page-padding-x !important;
    left: auto !important;
    background: var(--p-popover-background);
    border: none;
    border-radius: var(--p-popover-border-radius);
    box-shadow: var(--p-popover-shadow);
    overflow: hidden;
}

:global(.p-popover.user-menu-popover::before),
:global(.p-popover.user-menu-popover::after) {
    display: none !important;
}

:global(.p-popover.user-menu-popover .p-popover-content),
:global(.user-menu-popover__content) {
    display: flex;
    flex-direction: column;
    min-height: 117px;
    padding: var(--p-popover-content-padding);
    background: transparent;
}
</style>
